/**
 * Component Tracking Composable
 *
 * Provides standardized tracking for marketing components.
 * Automatically includes marketing context (landing page, personalization)
 * in all tracked events.
 *
 * Supports visibility-based tracking using IntersectionObserver:
 * - trackOnVisible: true (default) - Track only when component is visible
 * - trackOnMount: false (default) - Track on component mount (legacy)
 *
 * IMPORTANT: Must be used inside setup() or components only.
 *
 * Usage:
 * ```typescript
 * const { elementRef, trackComponentView, trackCtaClick } = useComponentTracking({
 *   componentName: 'MarketingHero',
 *   sectionId: 'hero',
 *   sectionType: 'hero',
 *   trackOnVisible: true, // Track only when visible (default)
 * })
 *
 * // Track component view on visibility
 * const { elementRef } = useComponentTracking(...)
 *
 * // In template:
 * <div ref="elementRef">Content</div>
 *
 * // Track interaction
 * const handleClick = () => {
 *   trackCtaClick('Start Test', { position: 1 })
 * }
 * ```
 */

import { ref, onMounted } from 'vue'
import { useTracking } from './useTracking'
import { getMarketingContextData, getConcernTrackingData } from './useMarketingContext'
import { useVisibilityTracking } from './useVisibilityTracking'
import { TrackingEventType } from '@monorepo/types'

export interface ComponentTrackingOptions {
  componentName: string
  sectionId?: string
  sectionType?: string
  /**
   * Track component view when it becomes visible (default: true)
   * Uses IntersectionObserver for accurate view tracking
   */
  trackOnVisible?: boolean
  /**
   * Track component view on mount (legacy, default: false)
   * Set to true for backward compatibility
   */
  trackOnMount?: boolean
  /**
   * Visibility threshold (0-1, default: 0.5)
   * 0.5 = 50% visible (industry standard)
   */
  visibilityThreshold?: number
}

/**
 * Metadata for component tracking events
 * Properly typed to prevent spreading issues
 */
interface ComponentEventMetadata extends Record<string, string | number | boolean> {
  component_name: string
  section_id: string
  section_type: string
  event_name: string
}

/**
 * Track component interactions with automatic context
 * MUST be called inside setup() or components only
 *
 * DEFENSIVE: Never breaks component functionality if tracking fails
 */
export function useComponentTracking(options: ComponentTrackingOptions) {
  const tracking = useTracking()
  const elementRef = ref<HTMLElement | null>(null)

  // Normalize options
  const trackOnVisible = options.trackOnVisible !== false // default: true
  const trackOnMount = options.trackOnMount === true // default: false
  const visibilityThreshold = options.visibilityThreshold ?? 0.5

  // Capture context during setup phase (when useRoute() is available)
  // Use defensive approach: if context retrieval fails, tracking still works
  let cachedMarketingContext: Record<string, string | number | boolean> = {}
  let cachedConcernData: Record<string, string | number> = {}

  try {
    cachedMarketingContext = getMarketingContextData()
  } catch (error) {
    // Silently fail - tracking context is optional
    // Component functionality is never affected
    if (import.meta.env.DEV) {
      console.warn('[useComponentTracking] Failed to get marketing context:', error)
    }
  }

  try {
    cachedConcernData = getConcernTrackingData()
  } catch (error) {
    // Silently fail - tracking context is optional
    // Component functionality is never affected
    if (import.meta.env.DEV) {
      console.warn('[useComponentTracking] Failed to get concern data:', error)
    }
  }

  /**
   * Track a component event with automatic context
   * DEFENSIVE: Never throws, always succeeds
   * Component functionality is never affected by tracking failures
   */
  const trackComponentEvent = (
    eventName: string,
    eventData: Record<string, string | number | boolean> = {}
  ): void => {
    try {
      const metadata: ComponentEventMetadata = {
        component_name: options.componentName,
        section_id: options.sectionId || 'unknown',
        section_type: options.sectionType || 'unknown',
        event_name: eventName,
        ...cachedMarketingContext,
        ...cachedConcernData,
        ...eventData,
      }

      // Log in development
      if (import.meta.env.DEV) {
        console.log(`[Component Tracking] ${options.componentName}:${eventName}`, metadata)
      }

      // Create properly typed event payload
      const pageUrl = typeof window !== 'undefined' ? window.location.href : 'unknown'

      // Track as engagement event
      tracking.trackEngagementEvent({
        eventType: TrackingEventType.PAGE_ENGAGEMENT,
        pageUrl,
        metadata,
      })
    } catch (error) {
      // DEFENSIVE: Never throw, never break component
      // Silently fail - tracking is not critical
      if (import.meta.env.DEV) {
        console.warn(`[Component Tracking] Failed to track ${eventName}:`, error)
      }
    }
  }

  /**
   * Track component view/mount
   * Can be called manually or automatically via visibility tracking
   */
  const trackComponentView = (additionalData: Record<string, string | number | boolean> = {}): void => {
    trackComponentEvent('component_viewed', {
      action: 'view',
      tracking_method: trackOnVisible ? 'visibility' : 'mount',
      ...additionalData,
    })
  }

  /**
   * Track CTA click within component
   */
  const trackCtaClick = (ctaText: string, additionalData: Record<string, string | number | boolean> = {}): void => {
    trackComponentEvent('cta_clicked', {
      cta_text: ctaText,
      action: 'cta_click',
      ...additionalData,
    })
  }

  /**
   * Track form interaction
   */
  const trackFormInteraction = (
    fieldName: string,
    interactionType: 'focus' | 'blur' | 'change' | 'submit',
    additionalData: Record<string, string | number | boolean> = {}
  ): void => {
    trackComponentEvent('form_interaction', {
      field_name: fieldName,
      interaction_type: interactionType,
      ...additionalData,
    })
  }

  /**
   * Track accordion/expandable interaction
   */
  const trackExpandableInteraction = (
    itemId: string,
    isExpanded: boolean,
    additionalData: Record<string, string | number | boolean> = {}
  ): void => {
    trackComponentEvent('expandable_toggled', {
      item_id: itemId,
      is_expanded: isExpanded,
      action: isExpanded ? 'expand' : 'collapse',
      ...additionalData,
    })
  }

  /**
   * Track scroll/engagement within component
   */
  const trackEngagement = (
    engagementType: string,
    additionalData: Record<string, string | number | boolean> = {}
  ): void => {
    trackComponentEvent('engagement', {
      engagement_type: engagementType,
      ...additionalData,
    })
  }

  /**
   * Setup visibility tracking if enabled
   */
  if (trackOnVisible) {
    useVisibilityTracking({
      elementRef, // Pass our elementRef to visibility tracking
      threshold: visibilityThreshold,
      onFirstView: () => {
        trackComponentView()
      },
      debug: import.meta.env.DEV,
    })
  }

  /**
   * Setup mount-based tracking if enabled (legacy)
   */
  if (trackOnMount) {
    onMounted(() => {
      trackComponentView()
    })
  }

  return {
    elementRef,
    trackComponentEvent,
    trackComponentView,
    trackCtaClick,
    trackFormInteraction,
    trackExpandableInteraction,
    trackEngagement,
  }
}

/**
 * Simplified version for use in templates via TrackClick component
 * Automatically adds component context to event data
 * MUST be called inside setup() or components only
 */
export function getComponentTrackingData(
  componentName: string,
  sectionId?: string,
  sectionType?: string
): Record<string, string | number | boolean> {
  const marketingContext = getMarketingContextData()
  const concernData = getConcernTrackingData()

  return {
    component_name: componentName,
    section_id: sectionId || 'unknown',
    section_type: sectionType || 'unknown',
    ...marketingContext,
    ...concernData,
  }
}
